import type { ReactNode } from 'react'
import { Link } from '@inertiajs/react'
import { AlertTriangle, ArrowLeft, Pencil, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ShipmentStageRail } from './StageRail'
import type { HeaderScheduleItem, ShipmentHeader } from './types'
import {
  flattenBookingStatusItems,
  formatAttentionSummary,
  formatBookingCountSummary,
  formatBookingRowStatusLabel,
  formatCompactReviewLabel,
  formatContainerBookingScope,
  formatContainerPrimarySummary,
  formatScheduleHeaderValue,
  formatScheduleRefs,
  formatScheduleValue,
  formatUntaggedDocumentSummary,
  type HeaderBookingStatusItem,
  type TabName,
} from './helpers'

export default function ShipmentCommandHeader({
  shipmentHeader,
  pathFor,
  editing,
  activeTab,
  onEdit,
  onAddBooking,
  onSelectDocuments,
}: {
  shipmentHeader: ShipmentHeader
  pathFor: (path: string) => string
  editing: boolean
  activeTab: TabName
  onEdit: () => void
  onAddBooking: () => void
  onSelectDocuments: () => void
}) {
  const facts = shipmentHeader.facts
  const route = [facts.route.origin_port, facts.route.destination_port].filter(Boolean).join(' \u2192 ')
  const documentRollups = [...shipmentHeader.document_review_rollups.si, ...shipmentHeader.document_review_rollups.bl]
  const attentionRollups = documentRollups.filter((rollup) => rollup.review_outcome !== 'accepted')
  const firstScheduleItem = shipmentHeader.schedule_items[0]
  const hiddenScheduleItems = shipmentHeader.schedule_items.slice(1)
  const bookingStatusItems = flattenBookingStatusItems(shipmentHeader.booking_status_rollups)
  const hasAttention = shipmentHeader.untagged_document_count > 0 || attentionRollups.length > 0
  const attentionCount = shipmentHeader.untagged_document_count + attentionRollups.reduce((total, rollup) => total + rollup.count, 0)
  const metadataItems = [
    facts.client ? (
      <Link key="client" href={pathFor(`/clients/${facts.client.id}`)} className="font-medium text-blue-600 hover:underline cursor-pointer">
        {facts.client.name}
      </Link>
    ) : null,
    route ? <span key="route">{route}</span> : null,
    facts.commodity ? <span key="commodity">{facts.commodity}</span> : null,
  ].filter(Boolean)

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <Link href={pathFor('/shipments')} className="text-muted-foreground hover:text-foreground cursor-pointer" aria-label="Back to shipments">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-bold">{facts.internal_reference}</h1>
          {metadataItems.length > 0 && (
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-muted-foreground">
              {metadataItems.map((item, index) => (
                <span key={index} className="inline-flex items-center gap-x-2">
                  {index > 0 && <span className="text-muted-foreground/60">·</span>}
                  {item}
                </span>
              ))}
            </div>
          )}
        </div>
        {!editing && activeTab === 'Overview' && (
          <Button
            variant="outline"
            size="sm"
            className="text-xs cursor-pointer"
            onClick={onEdit}
          >
            <Pencil className="h-3.5 w-3.5 mr-1.5" />
            Edit
          </Button>
        )}
      </div>

      <ShipmentStageRail lifecycle={shipmentHeader.lifecycle} />

      <div className="grid gap-3 rounded-lg border bg-gray-50/60 p-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <HeaderSummaryBlock
          label="Attention needed"
          value={formatAttentionSummary(attentionCount)}
          labelIcon={hasAttention ? <AlertTriangle className="h-3 w-3 text-red-600" /> : undefined}
        >
          {hasAttention && (
            <div className="mt-1 space-y-0.5">
              {shipmentHeader.untagged_document_count > 0 && (
                <div className="flex items-start gap-1.5 text-[12px] leading-4 text-gray-700">
                  <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-gray-400" />
                  <button
                    type="button"
                    onClick={onSelectDocuments}
                    className="text-left text-blue-600 hover:underline cursor-pointer"
                  >
                    {formatUntaggedDocumentSummary(shipmentHeader.untagged_document_count)}
                  </button>
                </div>
              )}
              {attentionRollups.map((rollup) => (
                <div key={`${rollup.document_type}-${rollup.review_outcome}`} className="flex items-start gap-1.5 text-[12px] leading-4 text-gray-700">
                  <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-gray-400" />
                  <span>{rollup.count} {formatCompactReviewLabel(rollup)}</span>
                </div>
              ))}
            </div>
          )}
        </HeaderSummaryBlock>
        {facts.total_booking_count === 0 ? (
          <HeaderSummaryBlock
            label="Bookings"
            value="No bookings yet"
          >
            <Button variant="outline" size="sm" className="mt-1 h-7 text-xs cursor-pointer" onClick={onAddBooking}>
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add Booking
            </Button>
          </HeaderSummaryBlock>
        ) : (
          <HeaderSummaryBlock
            label="Bookings"
            value={formatBookingCountSummary(facts)}
          >
            {bookingStatusItems.length > 0 && (
              <details className="mt-0.5">
                <summary className="list-none text-[12px] font-medium text-blue-600 hover:underline cursor-pointer">
                  Show all
                </summary>
                <div className="mt-1 space-y-0.5">
                  {bookingStatusItems.map((item) => (
                    <BookingStatusSummaryLine key={`${item.status}-${item.bookingRef.booking_id}`} item={item} />
                  ))}
                </div>
              </details>
            )}
          </HeaderSummaryBlock>
        )}
        <HeaderSummaryBlock
          label="Containers"
          value={formatContainerPrimarySummary(shipmentHeader.container_rollups)}
          detail={formatContainerBookingScope(shipmentHeader.container_rollups)}
        />
        <HeaderSummaryBlock
          label="Next schedule"
          value={firstScheduleItem ? formatScheduleHeaderValue(firstScheduleItem) : 'No schedule yet'}
          detail={firstScheduleItem ? formatScheduleRefs(firstScheduleItem) : undefined}
        >
          {hiddenScheduleItems.length > 0 && (
            <details className="mt-0.5">
              <summary className="list-none text-[12px] font-medium text-blue-600 hover:underline cursor-pointer">
                +{hiddenScheduleItems.length} more schedule items
              </summary>
              <div className="mt-1 space-y-0.5">
                {hiddenScheduleItems.map((item) => (
                  <ScheduleSummaryLine key={`${item.key}-${item.value}-${item.source}`} item={item} />
                ))}
              </div>
            </details>
          )}
        </HeaderSummaryBlock>
        <HeaderSummaryBlock
          label="Linked activity"
          value={`${facts.thread_count} ${facts.thread_count === 1 ? 'thread' : 'threads'}`}
        />
      </div>
    </div>
  )
}

function HeaderSummaryBlock({ label, value, detail, labelIcon, children }: { label: string; value: string; detail?: string; labelIcon?: ReactNode; children?: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="mb-1.5 flex items-center gap-1 text-[11px] font-medium uppercase leading-3 text-muted-foreground">
        {labelIcon}
        {label}
      </div>
      <p className="text-[13px] font-semibold leading-4 text-foreground">{value}</p>
      {detail && <p className="text-[12px] leading-4 text-muted-foreground">{detail}</p>}
      {children}
    </div>
  )
}

function ScheduleSummaryLine({ item }: { item: HeaderScheduleItem }) {
  return (
    <div className="text-[12px] leading-4 text-gray-700">
      <span className="font-medium">{item.label}</span>
      <span> {formatScheduleValue(item)}</span>
      <span className="text-muted-foreground"> ({formatScheduleRefs(item)})</span>
    </div>
  )
}

function BookingStatusSummaryLine({ item }: { item: HeaderBookingStatusItem }) {
  return (
    <div className="text-[12px] leading-4 text-gray-700">
      <span className="font-medium">{item.bookingRef.internal_reference}</span>
      <span className="text-muted-foreground"> - {formatBookingRowStatusLabel(item.status)}</span>
    </div>
  )
}
